import type { BootstrapConfig } from '../api/types';
import type { ThreeDFile, ThreeDPart } from '../api/types';
import type { BuilderStep } from '../types/models';
import type { BuilderStore } from '../state/BuilderStore';
import type { StepId } from '../state/types';
import { StepRegistry } from './StepRegistry';
import { fetchModelDetail, resolveImageUrl } from '../api/client';
import { calculateTotal } from '../pricing/PriceCalculator';
import { formatCurrency } from '../pricing/PriceFormatter';
import { parseSettings } from '../config/BuilderConfig';
import { ThreeJsViewer } from '../image/ThreeJsViewer';
import { GradientMapCompositor } from '../image/GradientMapCompositor';

interface CachedModel {
  model_name: string;
  model_year: number;
  hero_image: string;
  header_image: string;
  thumbnail: string;
  base_msrp: number;
  base_sale_price: number;
  base_dealer_net: number;
  render_type: string;
  model_3d_file: ThreeDFile | null;
  model_3d_parts: ThreeDPart[];
  color_layers: Array<{ layer_type_id: number | null; image_path: string; z_index: number }>;
  engines: Array<{ id: number; engine_name: string; msrp_price: number; sale_price: number; dealer_net: number }>;
  option_sections: Array<{ options: Array<{ id: number; msrp_price: number; sale_price: number; dealer_net: number }> }>;
}

/**
 * Manages step sequencing, navigation, and rendering.
 *
 * Layout:
 *  - Step 1 (Collection/Model): full-width model browser
 *  - Steps 2+: two-column — persistent model image on left, sidebar on right.
 *    Sidebar contains: live price bar (top) → step content → prev/next nav (bottom).
 */
export class BuilderFlow {
  private steps: BuilderStep[] = [];
  private allSteps: BuilderStep[] = [];
  private currentStepInstance: BuilderStep | null = null;
  private _navigatingFromHistory = false;
  private sharedCanvas: HTMLCanvasElement | null = null;
  private sharedOverlayCanvas: HTMLCanvasElement | null = null;
  private canvasHasContent = false;
  private modelCache: CachedModel | null = null;
  private cachedModelId: number | null = null;
  /** Three.js viewer — active only when the loaded model has render_type '3d' */
  threeViewer: ThreeJsViewer | null = null;

  constructor(
    private container: HTMLElement,
    private store: BuilderStore,
    private config: BootstrapConfig
  ) { }

  /** Build the persistent shell, register steps, and navigate to step 1 */
  start(): void {
    this.container.innerHTML = `
      <div class="vc-builder">
        <nav class="vc-step-nav" id="vc-step-nav"></nav>
        <div class="vc-builder-body vc-layout-collection" id="vc-builder-body">
          <!-- Step 1: full-width model browser -->
          <main class="vc-step-content-full" id="vc-step-content-full"></main>
          <!-- Header image with model name (steps 2+) -->
          <div class="vc-model-header" id="vc-model-header">
            <div class="vc-model-header-text">
              <span class="vc-model-header-name" id="vc-model-header-name"></span>
              <span class="vc-model-header-year" id="vc-model-header-year"></span>
            </div>
          </div>
          <!-- Steps 2+: two-column customizer -->
          <div class="vc-customizer-cols" id="vc-customizer-cols">
            <div class="vc-model-panel" id="vc-model-panel">
              <img id="vc-model-hero" src="" alt="Model" class="vc-model-panel-img" />
              <div class="vc-canvas-wrap">
                <canvas id="vc-model-canvas" class="vc-model-panel-canvas"></canvas>
                <canvas id="vc-model-canvas-overlay" class="vc-model-canvas-overlay"></canvas>
              </div>
            </div>
            <aside class="vc-sidebar" id="vc-sidebar">
              <div class="vc-price-bar">
                <span class="vc-price-bar-label">Estimated Total</span>
                <span class="vc-price-bar-amount" id="vc-total-price">—</span>
              </div>
              <div class="vc-sidebar-content" id="vc-sidebar-content"></div>
              <div class="vc-sidebar-nav">
                <button class="vc-btn vc-btn-outline" id="vc-prev-btn">← Previous</button>
                <button class="vc-btn vc-btn-primary" id="vc-next-btn">Next →</button>
              </div>
            </aside>
          </div>
        </div>
      </div>
    `;

    this.sharedCanvas = document.getElementById('vc-model-canvas') as HTMLCanvasElement;
    this.sharedOverlayCanvas = document.getElementById('vc-model-canvas-overlay') as HTMLCanvasElement;

    this.allSteps = StepRegistry.build(this.config, this.store, () => {
      const next = this.getNextStep('collection' as StepId);
      if (next) this.navigateTo(next);
    }, this.sharedCanvas, this.sharedOverlayCanvas, () => this.threeViewer);
    this.steps = this.allSteps;

    // Subscribe: re-render nav + refresh price bar on any state change
    this.store.subscribe(() => {
      this.renderNav();
      this.refreshPriceBar();
    });

    // Bind persistent sidebar prev/next buttons
    this.bindSidebarNav();

    // Stamp initial history entry so browser back can return here
    history.replaceState({ vcStep: this.steps[0]?.id ?? '' }, '');

    // Handle browser back / forward
    window.addEventListener('popstate', (e: PopStateEvent) => {
      const stepId = (e.state as { vcStep?: string } | null)?.vcStep as StepId | undefined;
      if (stepId && this.steps.some(s => s.id === stepId)) {
        this._navigatingFromHistory = true;
        this.navigateTo(stepId);
        this._navigatingFromHistory = false;
      }
    });

    // Start on first step
    if (this.steps.length > 0) {
      this.navigateTo(this.steps[0].id as StepId);
    }
  }

  /** Navigate to a specific step */
  navigateTo(stepId: StepId): void {
    if (this.currentStepInstance) {
      this.currentStepInstance.onLeave();
      this.currentStepInstance.destroy();
    }

    // Push a history entry so the browser back button can retrace steps.
    // Skip when we're already responding to a popstate event.
    if (!this._navigatingFromHistory) {
      history.pushState({ vcStep: stepId }, '');
    }

    this.store.goToStep(stepId);

    const isCollection = stepId === 'collection';

    // Switch layout class on the body wrapper
    const body = document.getElementById('vc-builder-body');
    if (body) {
      body.classList.toggle('vc-layout-collection', isCollection);
      body.classList.toggle('vc-layout-customizer', !isCollection);
    }

    // Returning to collection resets the canvas so the next chosen model starts fresh
    if (isCollection) {
      this.canvasHasContent = false;
      const modelPanel = document.getElementById('vc-model-panel');
      modelPanel?.classList.remove('vc-show-canvas');

      // Wipe overlay canvas pixels so option layers from the previous model
      // don't bleed through when the user picks and previews a new model.
      if (this.sharedOverlayCanvas) {
        this.sharedOverlayCanvas.style.opacity = '0';
        const octx = this.sharedOverlayCanvas.getContext('2d');
        if (octx) octx.clearRect(0, 0, this.sharedOverlayCanvas.width, this.sharedOverlayCanvas.height);
      }
      // Also clear the main canvas so the colour step starts blank on the new model
      if (this.sharedCanvas) {
        const ctx = this.sharedCanvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, this.sharedCanvas.width, this.sharedCanvas.height);
      }
    }

    // Determine which container to render the step into
    const contentId = isCollection ? 'vc-step-content-full' : 'vc-sidebar-content';
    const content = document.getElementById(contentId);

    const step = this.steps.find(s => s.id === stepId);
    if (step && content) {
      content.innerHTML = '';
      step.render(content);
      step.onEnter();
      this.currentStepInstance = step;
    }

    // Show the canvas for every customizer step. The canvas is the canonical
    // preview surface; the static hero image is only a fallback while loading.
    if (!isCollection) {
      this.canvasHasContent = true;
    }
    const modelPanel = document.getElementById('vc-model-panel');
    if (modelPanel) {
      modelPanel.classList.toggle('vc-show-canvas', this.canvasHasContent);
    }

    // When entering customizer layout, load model data and refresh sidebar state
    if (!isCollection) {
      this.loadModelAndRefresh();
    }

    this.renderNav();
    this.updateSidebarNav();
  }

  /** Get the next step after the given stepId */
  getNextStep(currentId: StepId): StepId | null {
    const idx = this.steps.findIndex(s => s.id === currentId);
    if (idx >= 0 && idx < this.steps.length - 1) {
      return this.steps[idx + 1].id as StepId;
    }
    return null;
  }

  /** Get the previous step before the given stepId */
  getPreviousStep(currentId: StepId): StepId | null {
    const idx = this.steps.findIndex(s => s.id === currentId);
    if (idx > 0) {
      return this.steps[idx - 1].id as StepId;
    }
    return null;
  }

  // ── private ──────────────────────────────────────────────

  /** Wire up click events for persistent prev/next buttons and back-navigable step tabs (delegated, bound once) */
  private bindSidebarNav(): void {
    const builderBody = document.getElementById('vc-builder-body');
    builderBody?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('#vc-prev-btn')) {
        // Use history.back() so the browser's own history stack stays in sync.
        // popstate will fire and call navigateTo with _navigatingFromHistory=true.
        history.back();
        return;
      }
      if (target.closest('#vc-next-btn')) {
        const next = this.getNextStep(this.store.getState().currentStep as StepId);
        if (next) this.navigateTo(next);
      }
    });

    // Step nav is a sibling of vc-builder-body, so it needs its own listener
    const stepNav = document.getElementById('vc-step-nav');
    stepNav?.addEventListener('click', (e) => {
      const stepBtn = (e.target as HTMLElement).closest('[data-step-id]') as HTMLElement | null;
      if (stepBtn) {
        const stepId = stepBtn.dataset.stepId as StepId;
        if (stepId) this.navigateTo(stepId);
      }
    });
  }

  /** Show/hide prev and next buttons based on position in step list */
  private updateSidebarNav(): void {
    const currentId = this.store.getState().currentStep as StepId;
    const prevBtn = document.getElementById('vc-prev-btn') as HTMLButtonElement | null;
    const nextBtn = document.getElementById('vc-next-btn') as HTMLButtonElement | null;

    const prev = this.getPreviousStep(currentId);
    const next = this.getNextStep(currentId);

    if (prevBtn) prevBtn.style.visibility = prev ? 'visible' : 'hidden';
    if (nextBtn) nextBtn.style.display = next ? '' : 'none';
  }

  /**
   * Fetch (and cache) model detail for the currently selected model.
   * Updates the hero image and kicks off a price bar refresh.
   */
  private loadModelAndRefresh(): void {
    const modelId = this.store.getState().selectedModelId;
    if (!modelId) return;

    const finalize = (): void => {
      this.applyHeroImage();
      this.applyHeaderImage();
      this.refreshPriceBar();
      const redirected = this.updateActiveSteps();
      if (!redirected) {
        this.apply3dViewer();
        void this.ensureBaseImageOnCanvas();
      }
    };

    if (modelId === this.cachedModelId && this.modelCache) {
      finalize();
      return;
    }

    this.cachedModelId = modelId;
    fetchModelDetail(modelId)
      .then((detail) => {
        this.modelCache = detail as CachedModel;
        finalize();
      })
      .catch(() => { /* silently ignore */ });
  }

  /** Set the hero image src from cached model data */
  private applyHeroImage(): void {
    const heroImg = document.getElementById('vc-model-hero') as HTMLImageElement | null;
    const src = this.modelCache?.hero_image || this.modelCache?.thumbnail;
    if (heroImg) {
      heroImg.src = src ? resolveImageUrl(src) : resolveImageUrl(null);
      heroImg.alt = this.modelCache?.model_name ?? 'Model';
    }
  }

  /**
   * Mount or destroy the Three.js viewer based on the loaded model's render_type.
   * When render_type is '3d': hide the static hero image, show the canvas, and load the GLB.
   * When render_type is 'image': destroy any existing viewer so the canvas is free for 2D compositing.
   */
  private apply3dViewer(): void {
    const is3d = this.modelCache?.render_type === '3d';
    const heroImg = document.getElementById('vc-model-hero') as HTMLImageElement | null;
    const canvasWrap = this.sharedCanvas?.parentElement;

    if (!is3d) {
      // Destroy viewer and restore 2D canvas usage
      if (this.threeViewer) {
        this.threeViewer.destroy();
        this.threeViewer = null;
      }
      if (heroImg) heroImg.style.display = '';
      if (canvasWrap) canvasWrap.style.display = '';
      return;
    }

    // 3D mode: hide static hero, ensure canvas wrap is visible
    if (heroImg) heroImg.style.display = 'none';
    if (canvasWrap) canvasWrap.style.display = '';

    const canvas = this.sharedCanvas;
    if (!canvas) return;

    // Replace the canvas element with a fresh one — after destroy() the browser
    // marks the existing WebGL context as lost and refuses to create a new one
    // of the same type on the same element. Swapping it out gives Three.js a
    // clean canvas with no prior context.
    const freshCanvas = document.createElement('canvas');
    freshCanvas.id = canvas.id;
    freshCanvas.className = canvas.className;
    canvas.parentElement?.replaceChild(freshCanvas, canvas);
    this.sharedCanvas = freshCanvas;

    const glbPath = this.modelCache?.model_3d_file?.file_path;
    if (!glbPath) return;

    this.threeViewer = new ThreeJsViewer(this.sharedCanvas);
    const glbUrl = resolveImageUrl(glbPath);
    this.threeViewer.loadModel(glbUrl).catch(() => { /* silently ignore load errors */ });

    // Apply default part visibility from model_3d_parts
    for (const part of (this.modelCache?.model_3d_parts ?? [])) {
      if (!part.default_visible) {
        this.threeViewer.setPartVisible(part.mesh_name, false);
      }
    }
  }

  /** Set the header background image and model name/year text */
  private applyHeaderImage(): void {
    const header = document.getElementById('vc-model-header') as HTMLElement | null;
    if (header && this.modelCache?.header_image) {
      header.style.backgroundImage = `url('${resolveImageUrl(this.modelCache.header_image)}')`;    
      header.classList.add('vc-model-header--has-image');
    } else if (header) {
      header.style.backgroundImage = '';
      header.classList.remove('vc-model-header--has-image');
    }
    const nameEl = document.getElementById('vc-model-header-name');
    const yearEl = document.getElementById('vc-model-header-year');
    if (nameEl) nameEl.textContent = this.modelCache?.model_name ?? '';
    if (yearEl) yearEl.textContent = this.modelCache?.model_year ? String(this.modelCache.model_year) : '';
  }

  /**
   * When a model has no colorable layers (the color step is skipped), the shared
   * canvas would otherwise stay blank. Composite any static builder layers onto
   * the main canvas, falling back to the hero/thumbnail image only when the model
   * has no layers at all. This keeps the builder preview surface consistent with
   * the layered architecture instead of showing the marketing photo.
   */
  private async ensureBaseImageOnCanvas(): Promise<void> {
    if (!this.sharedCanvas || !this.modelCache) return;

    // 3D viewer manages its own canvas content; don't overwrite it.
    if (this.modelCache.render_type === '3d') return;

    // If the model has colorable layers, StepBoatColor will composite them onto
    // the canvas. Don't draw anything on top.
    if (this.hasColorOptions()) return;

    const staticLayers = (this.modelCache.color_layers ?? []).filter(
      l => l.layer_type_id === null && l.image_path
    );

    try {
      if (staticLayers.length > 0) {
        await this.drawStaticLayers(staticLayers, this.sharedCanvas);
      } else {
        await this.drawHeroImageOnCanvas();
      }

      // Re-check guards after the async load in case the user navigated away.
      if (!this.sharedOverlayCanvas || !this.sharedCanvas || !this.modelCache) return;
      if (this.modelCache.render_type === '3d' || this.hasColorOptions()) return;

      // Make sure the overlay is clean when we start with a fresh base image.
      this.sharedOverlayCanvas.width = this.sharedCanvas.width;
      this.sharedOverlayCanvas.height = this.sharedCanvas.height;
      const octx = this.sharedOverlayCanvas.getContext('2d');
      if (octx) octx.clearRect(0, 0, this.sharedOverlayCanvas.width, this.sharedOverlayCanvas.height);
      this.sharedOverlayCanvas.style.opacity = '0';
    } catch (err) {
      console.error('[BuilderFlow] failed to prepare base image on canvas:', err);
    }
  }

  /** Composite static (non-colorable) builder layers onto the target canvas. */
  private async drawStaticLayers(
    staticLayers: Array<{ image_path: string; z_index: number }>,
    target: HTMLCanvasElement
  ): Promise<void> {
    const layers = staticLayers
      .sort((a, b) => a.z_index - b.z_index)
      .map(l => ({
        src: resolveImageUrl(l.image_path),
        hexColor: '',
        zIndex: l.z_index,
      }));
    if (layers.length === 0) return;
    const compositor = new GradientMapCompositor();
    await compositor.composite(target, layers);
  }

  /** Draw the model's hero/thumbnail image onto the shared canvas as a fallback. */
  private async drawHeroImageOnCanvas(): Promise<void> {
    if (!this.sharedCanvas || !this.modelCache) return;
    const src = this.modelCache.hero_image || this.modelCache.thumbnail;
    if (!src) return;

    const img = await this.loadImage(resolveImageUrl(src));
    if (this.sharedCanvas.width !== img.naturalWidth || this.sharedCanvas.height !== img.naturalHeight) {
      this.sharedCanvas.width = img.naturalWidth;
      this.sharedCanvas.height = img.naturalHeight;
    }
    const ctx = this.sharedCanvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, this.sharedCanvas.width, this.sharedCanvas.height);
      ctx.drawImage(img, 0, 0);
    }
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load: ${src}`));
      img.src = src;
    });
  }

  /** Recalculate total price from current store state and update the price bar */
  private refreshPriceBar(): void {
    const priceEl = document.getElementById('vc-total-price');
    if (!priceEl) return;

    const state = this.store.getState();
    if (!this.modelCache || !state.selectedModelId) {
      priceEl.textContent = '—';
      return;
    }

    const settings = parseSettings(this.config);

    const basePrice = {
      msrp: this.modelCache.base_msrp,
      salePrice: this.modelCache.base_sale_price,
      dealerNet: this.modelCache.base_dealer_net,
    };

    const engine = this.modelCache.engines?.find(e => e.id === state.selectedEngineId);
    const enginePrice = engine
      ? { msrp: engine.msrp_price, salePrice: engine.sale_price, dealerNet: engine.dealer_net }
      : { msrp: 0, salePrice: 0, dealerNet: 0 };

    // Sum all selected color layer prices
    const colorPrice = Object.values(state.selectedColors).reduce(
      (acc, sel) => ({ msrp: acc.msrp + sel.msrpPrice, salePrice: acc.salePrice + sel.salePrice, dealerNet: acc.dealerNet + sel.dealerNet }),
      { msrp: 0, salePrice: 0, dealerNet: 0 }
    );

    const allOptions = (this.modelCache.option_sections ?? []).flatMap(s => s.options);
    const selectedOpts = allOptions
      .filter(o => state.selectedOptions.includes(o.id))
      .map(o => ({ id: o.id, msrp: o.msrp_price, salePrice: o.sale_price, dealerNet: o.dealer_net }));

    const total = calculateTotal(basePrice, enginePrice, colorPrice, selectedOpts, {
      exchangeRate: settings.exchangeRate,
      dealerBaseMargin: 0,
      dealerOptionsMargin: 0,
    });

    priceEl.textContent = formatCurrency(total.msrp, settings.locale, settings.currency);
  }

  /**
   * Rebuild the active steps list based on the current model.
   * Excludes the engine step when the selected model has no engines,
   * and excludes the color step when the model has no colorable layers.
   * @returns true if the current step was removed and a redirect happened.
   */
  private updateActiveSteps(): boolean {
    const hasEngines = (this.modelCache?.engines?.length ?? 0) > 0;
    const hasColors = this.hasColorOptions();

    this.steps = this.allSteps.filter(s => {
      if (s.id === 'engine') return hasEngines;
      if (s.id === 'boat-color') return hasColors;
      return true;
    });

    const currentId = this.store.getState().currentStep as StepId;
    if (!this.steps.some(s => s.id === currentId)) {
      // Current step was removed; redirect to the next available step.
      const currentAllIndex = this.allSteps.findIndex(s => s.id === currentId);
      const nextStep = this.allSteps
        .slice(currentAllIndex + 1)
        .find(s => this.steps.some(step => step.id === s.id));
      if (nextStep) {
        this.navigateTo(nextStep.id as StepId);
        return true;
      }
    }

    this.renderNav();
    this.updateSidebarNav();
    return false;
  }

  /** Returns true if the selected model has at least one colorable layer or 3D part color mapping. */
  private hasColorOptions(): boolean {
    if (!this.modelCache) return true; // default to showing until data loads

    if (this.modelCache.render_type === '3d') {
      return (this.modelCache.model_3d_parts ?? []).some(
        p => (p.color_mappings ?? []).length > 0
      );
    }

    return (this.modelCache.color_layers ?? []).some(l => l.layer_type_id !== null);
  }

  /** Render the informational step tab bar (no click handlers) */
  private renderNav(): void {
    const nav = document.getElementById('vc-step-nav');
    if (!nav) return;

    const state = this.store.getState();
    const currentIndex = this.steps.findIndex(s => s.id === state.currentStep);

    nav.innerHTML = this.steps
      .map((s, index) => {
        const isActive = s.id === state.currentStep;
        const isCompleted = index < currentIndex;
        const classes = ['vc-step-btn', isActive ? 'active' : '', isCompleted ? 'completed' : ''].filter(Boolean).join(' ');
        const dataAttr = isCompleted ? ` data-step-id="${s.id}"` : '';
        return `
          <div class="${classes}"${dataAttr}>
            <span class="vc-step-number">${index + 1}</span>
            <span class="vc-step-label">${s.label}</span>
          </div>
        `;
      })
      .join('');
  }
}
